import { useState, useEffect } from "react";
import { Form, Radio, Checkbox, Modal, Input, Button } from "antd";
import { useTranslation } from "../../../../utils/onboardingI18n";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LogoutApi } from "../../../../api/request/users";
import { removeCookies } from "../../../../services/Cookies";
import { setloggedIn } from "../../../../store/Slices/userSlice";
import { IpProvider } from "../../../../services/IpProvider";
import { addUpdateDeclaration, getDeclarationInfo } from "../../../../api/request/individualOnboarding";
import { handleApiError } from "../../../../utils/handleApiError.js";
import toast from "react-hot-toast";


const Declaration = ({ onNext, onPrevious, isSubmitted }) => {
  const { t } = useTranslation("onBoarding");
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ipAddress, setIpAddress] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [declarationId, setDeclarationId] = useState(null);

  const isDisabled = isSubmitted;

  // Watch form values for conditional displays
  const relationshipStatus = Form.useWatch("relationshipStatus", form);
  const openAccountChecked = Form.useWatch("openAccount", form);
  const cancelChecked = Form.useWatch("cancel", form);

  useEffect(() => {
    getIpAddress();
  }, []);

  useEffect(() => {
    if (cancelChecked) {
      setIsModalOpen(true);
    }
  }, [cancelChecked]);



  const loadDeclarationInfo  = async () =>{
      try{
        const { data } = await getDeclarationInfo();
        if(data?.success){
          // Store the id if it exists
          if(data?.data?.id){
            setDeclarationId(data.data.id);
          }

          // Map boolean isRelationship to string value
          const relationshipStatusValue = data?.data?.isRelationship
            ? "hasRelationship"
            : "noRelationship";

          form.setFieldsValue({
            relationshipStatus: relationshipStatusValue,
            relationship: data?.data?.relationName || "",
            confirmAssetsNotIllegal: data?.data?.confirmAssetsNotIllegal ?? false,
            confirmNotBreachAml: data?.data?.confirmNotBreachAml ?? false,
            confirmNoConviction: data?.data?.confirmNoConviction ?? false,
            confirmFundsTaxed: data?.data?.confirmFundsTaxed ?? false,
            agreeClientAgreement: data?.data?.agreeClientAgreement ?? false,
            declareInfoTrue: data?.data?.declareInfoTrue ?? false,
            confirmVoluntaryRegistration: data?.data?.confirmVoluntaryRegistration ?? false,
            undertakeNotifyChanges: data?.data?.undertakeNotifyChanges ?? false,
            openAccount: true,
            privacyPolicy: true,
          });
        }
      }catch(err){
        console.log('Loading delcarationInfo Error', err)
      }
  }

  useEffect(() => {
    loadDeclarationInfo();
  }, []);



  const getIpAddress = async () => {
    try {
      const { data } = await IpProvider();
      setIpAddress(data?.ip);
    } catch (error) {
      console.error("Error fetching IP address:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await LogoutApi({ ipAddress });
      localStorage.clear();
      dispatch(setloggedIn(false));
      removeCookies("token");
      navigate("/");
      setIsModalOpen(false);
    } catch (err) {
      dispatch(setloggedIn(false));
      removeCookies("token");
      navigate("/");
      setIsModalOpen(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.setFieldsValue({ cancel: false });
  };

  const handleSubmit = async (values) => {
    if (isDisabled) return;
    try {
      setIsSubmitLoading(true);

      // Build payload with only 2 fields
      const payload = {
        isRelationship: values.relationshipStatus === "hasRelationship",
        relationName: values.relationship || "",
        confirmAssetsNotIllegal: values.confirmAssetsNotIllegal,
        confirmNotBreachAml: values.confirmNotBreachAml,
        confirmNoConviction: values.confirmNoConviction,
        confirmFundsTaxed: values.confirmFundsTaxed,
        agreeClientAgreement: values.agreeClientAgreement,
        declareInfoTrue: values.declareInfoTrue,
        confirmVoluntaryRegistration: values.confirmVoluntaryRegistration,
        undertakeNotifyChanges: values.undertakeNotifyChanges,
      };

      // Include id only if it exists (for update operations)
      if (declarationId) {
        payload.id = declarationId;
      }

      const { data } = await addUpdateDeclaration(payload);

      if (data?.success) {
        toast.success(data?.message || t("Declaration information saved successfully"));
        onNext();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSubmitLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Form
        form={form}
        size="large"
        layout="vertical"
        autoComplete="off"
        disabled={isDisabled}
        onFinish={handleSubmit}
        initialValues={{
          relationshipStatus: "noRelationship",
        }}
      >
        <div className="border border-gray-300 rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">{t("Declaration Info")}</h2>

          {/* Relationship Disclosure Section */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              {t("As a regulated entity, we are committed to transparency and preventing conflicts of interest. We request that you disclose any existing relationships falling into the following categories:")}
            </p>

            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-2">
                <strong>{t("Direct connections:")}</strong> {t("Any relationships with CFC, its group companies or any of their board of directors, including personal or professional ties.")}
              </p>
              <p className="text-sm text-gray-700">
                <strong>{t("Market connections:")}</strong> {t("Any relationships with capital market institutions, publicly listed companies in the UAE, or other entities licensed by the SCA (including their boards of directors).")}
              </p>
            </div>

            <Form.Item
              name="relationshipStatus"
              rules={[
                { required: true, message: t("Please select a relationship status") },
              ]}
            >
              <Radio.Group disabled={isDisabled}>
                <Radio value="noRelationship">
                  {t("I confirm that I have no relationship with any of the above parties.")}
                </Radio>
                <Radio value="hasRelationship">
                  {t("I declare that I have the following relationship with.")}
                </Radio>
              </Radio.Group>
            </Form.Item>

            {/* Conditional Relationship Input */}
            {relationshipStatus === "hasRelationship" && (
              <Form.Item
                label={t("Relationship")}
                name="relationship"
                rules={[
                  {
                    required: true,
                    message: t("Please enter relationship details"),
                  },
                ]}
                className="mt-4"
              >
                <Input
                  placeholder={t("Enter relationship details")}
                  disabled={isDisabled}
                />
              </Form.Item>
            )}
          </div>

          {/* Declaration & Acknowledgement */}
          <div className="mb-6 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold mb-4">{t("Declaration & Acknowledgement")}</h3>
            <div className="space-y-3">
              {[
                ["confirmAssetsNotIllegal", t("I confirm that my assets are not derived from illegal activities.")],
                ["confirmNotBreachAml", t("I confirm that I will not breach AML regulations.")],
                ["confirmNoConviction", t("I confirm that I have no criminal convictions related to financial crime.")],
                ["confirmFundsTaxed", t("I confirm that my funds have been taxed in accordance with applicable laws.")],
                ["agreeClientAgreement", t("I agree to the Client Agreement and applicable terms.")],
                ["declareInfoTrue", t("I declare that the information provided is true and complete.")],
                ["confirmVoluntaryRegistration", t("I confirm that I am registering voluntarily and of my own accord.")],
                ["undertakeNotifyChanges", t("I undertake to notify UpForex of any material changes to the information provided.")],
              ].map(([name, label]) => (
                <Form.Item
                  key={name}
                  name={name}
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error(t("This confirmation is required"))),
                    },
                  ]}
                >
                  <Checkbox disabled={isDisabled}>{label}</Checkbox>
                </Form.Item>
              ))}
            </div>
          </div>

          {/* Product Appropriateness Section */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              {t("As responsible, regulated broker it is our duty to assess whether our products are appropriate for you.")}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t("Based on the information you have provided us about your knowledge, experience, and financial standing, we are able to offer you an account to trade Contracts for Difference (CFDs).")}
            </p>
            <p className="text-sm text-red-600 font-semibold mb-4">
              {t("Please remember that CFD trading carries a high level of risk, as prices can move rapidly against you, particularly during volatile market conditions.")}
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t("You will receive an email to the address you have registered with us with your account number.")}
            </p>
          </div>

          {/* Checkboxes Section */}
          <div className="space-y-4">
            <Form.Item
              name="openAccount"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            t("Please confirm that you would like to open an account")
                          )
                        ),
                },
              ]}
            >
              <Checkbox disabled={isDisabled}>
                {t("I Would Like To Open An Account And Understand The Risks Involved When Trading Cfds.")}
              </Checkbox>
            </Form.Item>

            {/* Conditional Privacy Policy Checkbox */}
            {openAccountChecked && (
              <Form.Item
                name="privacyPolicy"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              t("Please confirm that you have read the Privacy Policy and Terms and Conditions")
                            )
                          ),
                  },
                ]}
                className="ml-6"
              >
                <Checkbox disabled={isDisabled}>
                  <span>
                    {t("I confirm that I have read the")}{" "}
                    <a
                      href="https://www.upforex.com/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("Privacy Policy")}
                    </a>{" "}
                    {t("and")}{" "}
                    <a
                      href="https://www.upforex.com/legal-documents"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:text-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {t("Terms and Conditions")}
                    </a>
                    .
                  </span>
                </Checkbox>
              </Form.Item>
            )}

            <Form.Item name="cancel" valuePropName="checked">
              <Checkbox disabled={isDisabled}>
                {t("Cancel, I no longer wish to proceed.")}
              </Checkbox>
            </Form.Item>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-4 mt-6">
          {onPrevious && (
            <Button onClick={onPrevious} size="large" disabled={false}>
              {t("Previous")}
            </Button>
          )}
          {isDisabled ? (
            <Button type="primary" size="large" onClick={onNext} disabled={false}>
              {t("Next")}
            </Button>
          ) : (
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitLoading}
              disabled={cancelChecked || isSubmitLoading}
            >
              {isSubmitLoading ? t("Saving...") : t("Next")}
            </Button>
          )}
        </div>
      </Form>

      {/* Cancel Confirmation Modal */}
      <Modal
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        centered
        closable={false}
        rootClassName="onboarding-modal"
      >
        <div className="text-center py-4">
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-100 mb-4">
              <svg
                className="w-8 h-8 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-gray-800">
            {t("Cancel, I do not want to proceed")}
          </h3>
          <p className="text-gray-600 mb-6">
            {t("Are you sure you want to cancel the onboarding process.?")}
          </p>
          <div className="flex justify-center gap-4">
            <Button
              onClick={handleModalCancel}
              size="large"
              style={{
                borderColor: "#f97316",
                color: "#f97316",
              }}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="primary"
              onClick={handleLogout}
              size="large"
            >
              {t("YES")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Declaration;
